import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import './CartDrawer.css';
import API_BASE_URL from '../../config/api';

const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  return `${API_BASE_URL}${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
};

export default function CartDrawer() {
  const { items, totalPrice, totalItems, isDrawerOpen, setIsDrawerOpen, updateQuantity, removeItem } = useCart();
  const navigate = useNavigate();

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setIsDrawerOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [setIsDrawerOpen]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = isDrawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isDrawerOpen]);

  const handleCheckout = useCallback(() => {
    setIsDrawerOpen(false);
    navigate('/loja/checkout');
  }, [setIsDrawerOpen, navigate]);

  const formatPrice = (val) => `${Number(val).toFixed(2)} €`;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`cart-backdrop ${isDrawerOpen ? 'open' : ''}`}
        onClick={() => setIsDrawerOpen(false)}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside className={`cart-drawer ${isDrawerOpen ? 'open' : ''}`} aria-label="Carrinho de compras">
        <div className="cart-drawer-header">
          <h2 className="cart-drawer-title">
            <i className="fas fa-shopping-bag" />
            Carrinho
            {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
          </h2>
          <button
            className="cart-close-btn"
            onClick={() => setIsDrawerOpen(false)}
            aria-label="Fechar carrinho"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="cart-drawer-body">
          {items.length === 0 ? (
            <div className="cart-empty">
              <i className="fas fa-shopping-bag cart-empty-icon" />
              <p>O teu carrinho está vazio</p>
              <button
                className="btn btn-primary-blue"
                onClick={() => { setIsDrawerOpen(false); window.location.href = '/loja'; }}
              >
                Ir à Loja
              </button>
            </div>
          ) : (
            <ul className="cart-items-list">
              {items.map(item => {
                const uniqueId = item.cartItemId || item.variantId;
                return (
                  <li key={uniqueId} className="cart-item">
                    <div className="cart-item-image">
                      {item.imageUrl
                        ? <img src={getImageUrl(item.imageUrl)} alt={item.productName} loading="lazy" />
                        : <div className="cart-item-placeholder"><i className="fas fa-tshirt" /></div>
                      }
                    </div>
                    <div className="cart-item-info">
                      <p className="cart-item-name">{item.productName}</p>
                      <p className="cart-item-variant">
                        {item.variantDescription || [item.size, item.color].filter(Boolean).join(' · ')}
                      </p>
                      
                      {item.kitPieceSelections && item.kitPieceSelections.length > 0 && (
                        <div className="cart-item-kit-pieces" style={{ 
                          marginTop: '10px', 
                          padding: '10px', 
                          backgroundColor: '#f8fafc', 
                          borderRadius: '8px', 
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                        }}>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            <i className="fas fa-layer-group" style={{ color: 'var(--color-primary, #38bdf8)' }}></i>
                            Composição do Kit
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {item.kitPieceSelections.map((kp, idx) => (
                              <div key={`${kp.kitPieceId}_${kp.unitIndex}_${idx}`} style={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                fontSize: '11.5px',
                                paddingBottom: idx < item.kitPieceSelections.length - 1 ? '8px' : '0',
                                borderBottom: idx < item.kitPieceSelections.length - 1 ? '1px solid #e2e8f0' : 'none'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontWeight: '600', color: '#0f172a' }}>{kp.quantity || 1}x {kp.pieceName || `Peça ${kp.kitPieceId}`}</span>
                                  <span style={{ color: '#475569', fontWeight: '500', whiteSpace: 'nowrap' }}>
                                    Tam: <strong style={{ color: '#0f172a' }}>{kp.selectedSize}</strong>
                                  </span>
                                </div>
                                {(kp.customName || kp.customNumber) ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b', marginTop: '3px' }}>
                                    <i className="fas fa-pen" style={{ fontSize: '9px', opacity: 0.7 }}></i>
                                    <span>
                                      {kp.customName ? kp.customName : ''} 
                                      {kp.customName && kp.customNumber ? ' / ' : ''}
                                      {kp.customNumber ? `Nº ${kp.customNumber}` : ''}
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!item.kitPieceSelections && (item.customName || item.customNumber) && (
                        <p className="cart-item-customization" style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                          Personalização: {item.customName || '-'} / {item.customNumber ? `Nº ${item.customNumber}` : '-'}
                        </p>
                      )}
                      <div className="cart-item-controls">
                        <div className="cart-qty-controls">
                          <button
                            className="cart-qty-btn"
                            onClick={() => updateQuantity(uniqueId, item.quantity - 1)}
                            aria-label="Diminuir quantidade"
                          >−</button>
                          <span className="cart-qty-value">{item.quantity}</span>
                          <button
                            className="cart-qty-btn"
                            onClick={() => updateQuantity(uniqueId, item.quantity + 1)}
                            disabled={item.quantity >= item.stockQty}
                            aria-label="Aumentar quantidade"
                          >+</button>
                        </div>
                        <span className="cart-item-price">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    </div>
                    <button
                      className="cart-item-remove"
                      onClick={() => removeItem(uniqueId)}
                      aria-label={`Remover ${item.productName}`}
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="cart-drawer-footer">
            <div className="cart-subtotal">
              <span>Subtotal</span>
              <strong>{formatPrice(totalPrice)}</strong>
            </div>
            <p className="cart-shipping-note">
              <i className="fas fa-info-circle" /> Envio calculado no checkout
            </p>
            <button className="cart-checkout-btn" onClick={handleCheckout}>
              Finalizar Compra <i className="fas fa-arrow-right" />
            </button>
            <button
              className="cart-continue-btn"
              onClick={() => setIsDrawerOpen(false)}
            >
              Continuar a comprar
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
